import { useEffect, useMemo, useRef } from "react"
import { useStableContributorsCache } from "./use-stable-contributors-cache"
import { map, isEmpty } from "lodash"
import { LOADING, OK } from "helpers/data-statuses"
import { useIntradayPriceStream } from "connected-components/dashboard/hooks/use-intraday-price"
import { useIntradayStandalonePerformanceStream } from "connected-components/dashboard/hooks/use-intraday-standalone-performance"
import {
  buildIntradayFlatMap,
  buildIntradayStandaloneFlatMap,
} from "gatherers/contributors/intraday"
import { computeIntradayPricingSummary, getCurrentLocalDate } from "helpers/intraday-price-state"
import { collectLeafModelProviderIds } from "helpers/contributors"
import { enrichMetricsWithIntradayDelayTooltips } from "helpers/metrics"
import { stableCacheKey } from "connected-components/dashboard/helpers/query-key"
import {
  buildContributionSuffixIndexByModelProviderId,
  getActiveIntradayPricePrefix,
  getInstrumentSuffixForIntradayContributionPrefix,
  mergeFlatContributors,
  normalizeContributorKey,
  shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate,
  shouldSubscribeToIntradayPriceTicks,
} from "../contributors-data-integrator.utils"

const EMPTY_ARRAY = []

function getIntradayContributionGathererModelId(gatherer) {
  return gatherer?.modelId || gatherer?.variables?.modelId
}

export function useIntradayContributorsEnhancement({
  nonIntradayGatherers,
  intradayGatherers,
  metrics,
  data,
  status,
  isFetchingIntradayContributionQueries,
  isAnyQueryPending,
  isIntradayAnalyticsEnabled,
  instrumentsWithMetadata,
  queryClient,
}) {
  const intradayContributionQueryGatherers = useMemo(() => {
    return nonIntradayGatherers.filter((gatherer) =>
      getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix) != null,
    )
  }, [nonIntradayGatherers])
  const hasIntradayStreamingOrContributionGatherers = intradayGatherers.length > 0 || intradayContributionQueryGatherers.length > 0

  // Stable key identifies this logical query set independent of the asOf timestamp.
  // When the key changes (navigation to a different portfolio/instrument), cached data is discarded.
  const stableKey = useMemo(() => {
    const keys = nonIntradayGatherers.map(g => Array.isArray(g.key) ? g.key : [g.key])
    return stableCacheKey(keys)
  }, [nonIntradayGatherers])

  const cachedData = useStableContributorsCache(stableKey, data, isAnyQueryPending)
  const stableData = isAnyQueryPending ? cachedData : data

  const intradayPriceGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "prices")
  }, [intradayGatherers])
  const intradayStandaloneGatherers = useMemo(() => {
    return intradayGatherers.filter((gatherer) => gatherer.streamType === "standalone-performance")
  }, [intradayGatherers])

  const modelProviderIds = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !stableData?.children) {
      return EMPTY_ARRAY
    }
    return collectLeafModelProviderIds(stableData)
  }, [stableData, isIntradayAnalyticsEnabled, hasIntradayStreamingOrContributionGatherers])

  const shouldEnableIntraday =
    isIntradayAnalyticsEnabled
    && hasIntradayStreamingOrContributionGatherers
    && !!stableData
    && modelProviderIds.length > 0
  const shouldSubscribeToPriceStream = shouldSubscribeToIntradayPriceTicks({
    intradayPriceGatherers,
    intradayContributionQueryGatherers,
    intradayStandaloneGatherers,
  })
  // Drives the freshness dot. Resolved before the merge below so contribution-only and
  // standalone-only views (no price column) get priceAsOf merged under this prefix.
  // Standalone views subscribe to the price stream solely for the dot's per-row timestamp —
  // not for a visible column. Don't optimize this away until standalone payloads carry asOf.
  const hasActivePriceStream = shouldEnableIntraday && shouldSubscribeToPriceStream
  const intradayPricePrefix = useMemo(
    () => getActiveIntradayPricePrefix({ intradayPriceGatherers, hasActivePriceStream }),
    [intradayPriceGatherers, hasActivePriceStream],
  )
  const intradayStreamModelId =
    intradayPriceGatherers[0]?.modelId
    || getIntradayContributionGathererModelId(intradayContributionQueryGatherers[0])
    || intradayStandaloneGatherers[0]?.modelId

  const {
    prices: intradayPrices,
    updatedModelProviderIds: intradayUpdatedModelProviderIds,
  } = useIntradayPriceStream({
    modelProviderIds,
    modelId: intradayStreamModelId,
    enabled: shouldEnableIntraday && shouldSubscribeToPriceStream,
  })

  const standaloneSubscriptions = useMemo(() => {
    if (!shouldEnableIntraday || intradayStandaloneGatherers.length === 0) {
      return []
    }
    const periods = [...new Set(intradayStandaloneGatherers.map((gatherer) => gatherer.period).filter(Boolean))]
    return periods.map((period) => ({
      period,
      modelProviderIds,
    }))
  }, [intradayStandaloneGatherers, modelProviderIds, shouldEnableIntraday])

  const { standalonePerformanceByPeriod } = useIntradayStandalonePerformanceStream({
    subscriptions: standaloneSubscriptions,
    modelId: intradayStandaloneGatherers[0]?.modelId,
    enabled: shouldEnableIntraday && standaloneSubscriptions.length > 0,
  })

  const contributionQueryGatherersBySuffix = useMemo(() => {
    return intradayContributionQueryGatherers.reduce((gatherersBySuffix, gatherer) => {
      if (!shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate(gatherer)) {
        return gatherersBySuffix
      }

      const suffix = getInstrumentSuffixForIntradayContributionPrefix(gatherer.prefix)
      if (suffix == null) {
        return gatherersBySuffix
      }

      if (!gatherersBySuffix[suffix]) {
        gatherersBySuffix[suffix] = []
      }
      gatherersBySuffix[suffix].push(gatherer)
      return gatherersBySuffix
    }, {})
  }, [intradayContributionQueryGatherers])
  const contributionSuffixIndexByModelProviderId = useMemo(() => {
    return buildContributionSuffixIndexByModelProviderId(stableData)
  }, [stableData])

  // Refs let the effect always read the latest values without reacting to every data change.
  // This prevents a loop where background refetches update contributionSuffixIndexByModelProviderId,
  // which triggers the effect with stale intradayUpdatedModelProviderIds, causing another invalidation.
  const contributionGatherersRef = useRef(intradayContributionQueryGatherers)
  contributionGatherersRef.current = intradayContributionQueryGatherers
  const gatherersBySuffixRef = useRef(contributionQueryGatherersBySuffix)
  gatherersBySuffixRef.current = contributionQueryGatherersBySuffix
  const suffixIndexRef = useRef(contributionSuffixIndexByModelProviderId)
  suffixIndexRef.current = contributionSuffixIndexByModelProviderId

  useEffect(() => {
    if (!shouldEnableIntraday) {
      return
    }
    if (!contributionGatherersRef.current.length || !intradayUpdatedModelProviderIds.length) {
      return
    }

    const allSuffixes = Object.keys(gatherersBySuffixRef.current)
    if (!allSuffixes.length) {
      return
    }

    const impactedSuffixes = new Set()
    for (const modelProviderId of intradayUpdatedModelProviderIds) {
      const normalizedModelProviderId = normalizeContributorKey(modelProviderId)
      const suffixes = suffixIndexRef.current.get(modelProviderId)
        || suffixIndexRef.current.get(normalizedModelProviderId)
      if (suffixes && suffixes.size > 0) {
        suffixes.forEach((suffix) => impactedSuffixes.add(suffix))
      }
    }

    const suffixesToInvalidate = impactedSuffixes.size > 0 ? [...impactedSuffixes] : allSuffixes
    const gatherersToInvalidate = suffixesToInvalidate.flatMap((suffix) => gatherersBySuffixRef.current[suffix] || EMPTY_ARRAY)
    if (!gatherersToInvalidate.length) {
      return
    }

    gatherersToInvalidate.forEach((gatherer) => {
      queryClient.invalidateQueries({
        queryKey: gatherer.graphqlKey ?? gatherer.key,
        exact: true,
        refetchType: "active",
      })
    })
  }, [
    shouldEnableIntraday,
    intradayUpdatedModelProviderIds,
    queryClient,
  ])

  const dataWithIntraday = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers || !stableData) {
      return stableData
    }
    if (isEmpty(intradayPrices) && isEmpty(standalonePerformanceByPeriod)) {
      return stableData
    }

    const intradayPricesByRowId = buildIntradayFlatMap(intradayPrices)
    // With a price column, merge priceAsOf under each price gatherer's instrument.
    // Without one (contribution-only views), still surface it under the dot's prefix so
    // the freshness dot has a value to read even though no price column is displayed.
    const priceMergePrefixes = intradayPriceGatherers.length > 0
      ? intradayPriceGatherers.map((gatherer) => gatherer.prefix)
      : (intradayPricePrefix ? [intradayPricePrefix] : [])
    const priceContributors = map(priceMergePrefixes, (prefix) => ({
      prefix,
      valuesByRowId: intradayPricesByRowId,
    }))

    const standaloneFlatMapByPeriodKey = intradayStandaloneGatherers.reduce((flatMapCacheByPeriod, gatherer) => {
      const periodKey = String(gatherer.period)
      if (!flatMapCacheByPeriod.has(periodKey)) {
        flatMapCacheByPeriod.set(
          periodKey,
          buildIntradayStandaloneFlatMap(standalonePerformanceByPeriod, gatherer.period),
        )
      }
      return flatMapCacheByPeriod
    }, new Map())

    const standaloneContributors = map(intradayStandaloneGatherers, (gatherer) => ({
      prefix: gatherer.prefix,
      valuesByRowId: standaloneFlatMapByPeriodKey.get(String(gatherer.period)) || {},
    }))

    return mergeFlatContributors(stableData, [...priceContributors, ...standaloneContributors])
  }, [
    stableData,
    intradayPrices,
    intradayPriceGatherers,
    intradayPricePrefix,
    intradayStandaloneGatherers,
    standalonePerformanceByPeriod,
    isIntradayAnalyticsEnabled,
    hasIntradayStreamingOrContributionGatherers,
  ])

  // When we already have data from stableData, a pending status is a background refresh — suppress
  // the skeleton by returning OK so the existing rows remain visible.
  const effectiveStatus = useMemo(() => {
    if (status === LOADING && dataWithIntraday) {
      return OK
    }
    return status
  }, [status, dataWithIntraday])

  // Viewer-local "today" — fed into the figure below so it recomputes when the
  // viewer crosses midnight. Cells/rows derive Today/Prior at render time, and the
  // footer must track that boundary even without a fresh socket merge.
  const localToday = getCurrentLocalDate()

  // "Priced today" figure over the original instrument's weights when present
  // (suffix-less prefixes), else the first active instrument's (e.g. comparison-only
  // views); recomputes per tick because dataWithIntraday changes identity then. Gated on
  // a real price column — contribution-only, standalone-only, and freshness-only forced
  // views don't carry this figure; it stays tied to the Price Freshness column.
  const intradayPricingSummary = useMemo(() => {
    // !intradayPricePrefix is load-bearing, not redundant: it also guards the case where
    // price gatherers are wired but the stream isn't active yet (shouldEnableIntraday
    // false, e.g. modelProviderIds still empty on initial load) — prefix is null then.
    // freshnessOnly gatherers supply the dot's timestamp but don't represent a visible column.
    const hasRealPriceColumn = intradayPriceGatherers.some((g) => !g.freshnessOnly)
    if (!hasRealPriceColumn || !intradayPricePrefix || !dataWithIntraday) {
      return null
    }
    const instrumentSuffix = intradayPricePrefix.slice("intraday".length)
    return computeIntradayPricingSummary(dataWithIntraday, {
      intradayPrefix: intradayPricePrefix,
      compositionPrefix: `composition${instrumentSuffix}`,
      today: localToday,
    })
  }, [intradayPriceGatherers, intradayPricePrefix, dataWithIntraday, localToday])

  const metricsWithIntradayTooltip = useMemo(() => {
    if (!isIntradayAnalyticsEnabled || !hasIntradayStreamingOrContributionGatherers) {
      return metrics
    }
    return enrichMetricsWithIntradayDelayTooltips(metrics, { instrumentsWithMetadata })
  }, [metrics, isIntradayAnalyticsEnabled, hasIntradayStreamingOrContributionGatherers, instrumentsWithMetadata])

  return {
    data: dataWithIntraday,
    status: effectiveStatus,
    metrics: metricsWithIntradayTooltip,
    modelProviderIds,
    isIntradayContributionRefreshing: isFetchingIntradayContributionQueries && effectiveStatus === OK,
    intradayPricingSummary,
    intradayPricePrefix,
  }
}
